<script setup lang="ts">
/**
 * The impacts, risks and opportunities, one card each.
 *
 * A card rather than a row of inputs, because five fields across a rail this
 * narrow leaves each of them too small to read what is in it. The three closed
 * choices are drop-downs: they are closed lists, so anything that let somebody
 * type would let them type a fourth value the standards do not have.
 */
import { useI18n } from 'vue-i18n'

import { newIroEntry, type IroEntry, type IroSection } from '@/domain/section'
import { IRO_HORIZONS, IRO_POSITIONS, IRO_TYPES } from '@/sections/iro'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: IroSection }>()
const emit = defineEmits<{ change: [section: IroSection] }>()

function set(changes: Partial<IroSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setEntry(id: string, changes: Partial<IroEntry>): void {
  set({
    entries: props.section.entries.map((entry) =>
      entry.id === id ? { ...entry, ...changes } : entry,
    ),
  })
}

function add(): void {
  set({ entries: [...props.section.entries, newIroEntry()] })
}

function remove(id: string): void {
  set({ entries: props.section.entries.filter((entry) => entry.id !== id) })
}

function chosen(event: Event): string {
  return (event.target as HTMLSelectElement).value
}
</script>

<template>
  <div class="fields">
    <div class="entries">
      <div v-for="entry in section.entries" :key="entry.id" class="entry">
        <div class="entry__head">
          <input
            class="b-input entry__topic"
            :value="entry.topic"
            :placeholder="t('inspector.iro.topicPlaceholder')"
            @input="setEntry(entry.id, { topic: ($event.target as HTMLInputElement).value })"
          />
          <button
            type="button"
            class="b-icon-btn b-btn--danger"
            :aria-label="t('inspector.iro.removeEntry')"
            @click="remove(entry.id)"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div class="entry__choices">
          <select
            class="b-select"
            :value="entry.type"
            :aria-label="t('inspector.iro.type')"
            @change="setEntry(entry.id, { type: chosen($event) as IroEntry['type'] })"
          >
            <option v-for="option in IRO_TYPES" :key="option" :value="option">
              {{ t(`inspector.iro.types.${option}`) }}
            </option>
          </select>

          <select
            class="b-select"
            :value="entry.position"
            :aria-label="t('inspector.iro.position')"
            @change="setEntry(entry.id, { position: chosen($event) as IroEntry['position'] })"
          >
            <option v-for="option in IRO_POSITIONS" :key="option" :value="option">
              {{ t(`inspector.iro.positions.${option}`) }}
            </option>
          </select>

          <select
            class="b-select"
            :value="entry.horizon"
            :aria-label="t('inspector.iro.horizon')"
            @change="setEntry(entry.id, { horizon: chosen($event) as IroEntry['horizon'] })"
          >
            <option v-for="option in IRO_HORIZONS" :key="option" :value="option">
              {{ t(`inspector.iro.horizons.${option}`) }}
            </option>
          </select>
        </div>

        <input
          class="b-input"
          :value="entry.conclusion"
          :placeholder="t('inspector.iro.conclusionPlaceholder')"
          @input="setEntry(entry.id, { conclusion: ($event.target as HTMLInputElement).value })"
        />
      </div>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.iro.addEntry') }}
    </button>

    <InspectorField :label="t('inspector.iro.caption')">
      <input
        class="b-input"
        :value="section.caption"
        @input="set({ caption: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.entries {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.entry {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-4);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

.entry__head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.entry__topic {
  flex: 1;
  min-width: 0;
}

/* Three closed choices, side by side because they are read together: what it
   is, where it is, and when. */
.entry__choices {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sp-2);
}

.entry__choices .b-select {
  min-width: 0;
  padding-left: var(--sp-4);
  font-size: var(--text-sm);
}
</style>
